import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  DndContext,
  closestCenter,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import {
  MdAdd,
  MdArrowDownward,
  MdArrowDropDown,
  MdArrowUpward,
  MdClose,
  MdDelete,
  MdDownload,
  MdGridView,
  MdLock,
  MdPictureAsPdf,
  MdRefresh,
  MdRemove,
  MdUploadFile,
  MdVerticalAlignBottom,
  MdVerticalAlignTop,
} from 'react-icons/md';
import { usePdfOrganizer } from '../hooks/usePdfOrganizer';
import { readFileAsArrayBuffer, exportReorderedPdf } from '../utils/pdfExport';
import PageThumbnail from './PageThumbnail';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const ZOOM_PRESETS = [10, 25, 50, 75, 100, 110, 125, 150, 200];
const BASE_CANVAS_W = 180;

// ─── Upload empty state ───────────────────────────────────────────────────────

const UploadArea = ({ onDrop, isLoading }) => {
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    disabled: isLoading,
    onDrop: (accepted) => { if (accepted[0]) onDrop(accepted[0]); },
  });

  return (
    <Flex flex={1} align="center" justify="center" p={8}>
      <Box
        {...getRootProps()}
        bg={isDragActive ? 'blue.50' : 'white'}
        borderWidth="2px"
        borderStyle="dashed"
        borderColor={isDragActive ? 'blue.400' : 'gray.200'}
        borderRadius="20px"
        p={{ base: 10, md: 14 }}
        textAlign="center"
        cursor={isLoading ? 'wait' : 'pointer'}
        transition="all 0.2s ease"
        shadow="sm"
        w="full"
        maxW="560px"
        _hover={isLoading ? {} : { borderColor: 'blue.300', bg: 'blue.50', shadow: 'md' }}
      >
        <input {...getInputProps()} />
        <VStack spacing={5}>
          <MotionBox animate={isDragActive ? { scale: 1.1 } : { scale: 1 }} transition={{ duration: 0.2 }}>
            <Box
              w={16} h={16}
              bg={isDragActive ? 'blue.100' : 'gray.100'}
              borderRadius="2xl"
              display="flex" alignItems="center" justifyContent="center"
              mx="auto" transition="background 0.2s"
            >
              <Icon as={MdGridView} boxSize={8} color={isDragActive ? 'blue.500' : 'gray.400'} transition="color 0.2s" />
            </Box>
          </MotionBox>
          <VStack spacing={1}>
            <Text fontWeight="700" fontSize="lg" color="gray.800">
              {isDragActive ? 'Release to open PDF' : 'Drop your PDF here'}
            </Text>
            <Text fontSize="sm" color="gray.400">
              or click to select a file from your device
            </Text>
          </VStack>
          <Button
            colorScheme="blue" borderRadius="xl" px={8} fontWeight="600"
            isLoading={isLoading} loadingText="Loading PDF…"
            onClick={(e) => { e.stopPropagation(); open(); }}
            pointerEvents={isLoading ? 'none' : 'auto'}
          >
            Select PDF
          </Button>
          <Flex align="center" gap={1.5} color="gray.400">
            <Icon as={MdLock} boxSize={3} />
            <Text fontSize="xs">Processed locally in your browser · never uploaded</Text>
          </Flex>
        </VStack>
      </Box>
    </Flex>
  );
};

// ─── Drag overlay placeholder ─────────────────────────────────────────────────

const DragOverlayCard = ({ pageNumber }) => (
  <Box
    w="168px"
    bg="white"
    borderRadius="14px"
    borderWidth="2px"
    borderColor="blue.400"
    shadow="2xl"
    overflow="hidden"
    style={{ transform: 'rotate(2.5deg) scale(1.04)' }}
    opacity={0.95}
  >
    <Box mt={3} bg="blue.50" h="200px" display="flex" alignItems="center" justifyContent="center">
      <Icon as={MdPictureAsPdf} boxSize={10} color="blue.200" />
    </Box>
    <Flex justify="center" py={1.5}>
      <Text fontSize="10px" fontWeight="600" color="blue.400">{pageNumber}</Text>
    </Flex>
  </Box>
);

// ─── Empty state after all pages deleted ──────────────────────────────────────

const AllPagesRemovedState = ({ onReset }) => (
  <Flex direction="column" align="center" justify="center" minH="300px" gap={5}>
    <Box w={16} h={16} bg="gray.100" borderRadius="2xl" display="flex" alignItems="center" justifyContent="center">
      <Icon as={MdPictureAsPdf} boxSize={8} color="gray.300" />
    </Box>
    <VStack spacing={1} textAlign="center">
      <Text fontWeight="700" color="gray.600" fontSize="md">All pages removed</Text>
      <Text fontSize="sm" color="gray.400" maxW="300px">Load a new PDF to start over.</Text>
    </VStack>
    <Button leftIcon={<MdUploadFile />} colorScheme="blue" variant="outline" borderRadius="xl" size="sm" onClick={onReset}>
      Load another PDF
    </Button>
  </Flex>
);

// ─── Selection action bar ─────────────────────────────────────────────────────

const SelectionActionBar = ({ count, onMoveToFirst, onMoveUp, onMoveDown, onMoveToLast, onDelete, onClear }) => {
  const actionBtn = (label, BtnIcon, handler, scheme = 'gray') => (
    <Tooltip label={label} placement="top" hasArrow openDelay={400} fontSize="xs">
      <IconButton
        icon={<BtnIcon size={16} />} aria-label={label} size="xs" variant="ghost"
        colorScheme={scheme} borderRadius="lg" h={7} w={7} minW={7}
        _hover={{ bg: scheme === 'red' ? 'red.50' : 'gray.100' }}
        onClick={handler}
      />
    </Tooltip>
  );

  return (
    <MotionFlex
      initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.18, ease: 'easeOut' }}
      align="center" gap={1.5} px={3} py={2} bg="white"
      borderWidth="1px" borderColor="blue.100" borderRadius="14px"
      shadow="0 2px 12px rgba(66,153,225,0.1)" flexWrap="wrap" mb={4}
    >
      <Flex align="center" gap={2} flexShrink={0} mr={1}>
        <Box as="span" display="inline-flex" alignItems="center" justifyContent="center" minW={5} h={5} bg="blue.500" borderRadius="full" px={1.5}>
          <Text as="span" fontSize="10px" fontWeight="700" color="white">{count}</Text>
        </Box>
        <Text as="span" fontSize="xs" color="gray.500" fontWeight="500">
          {count === 1 ? 'page' : 'pages'} selected
        </Text>
      </Flex>
      <Box w="1px" h={4} bg="gray.100" flexShrink={0} mx={0.5} />
      <HStack spacing={0}>
        {actionBtn('Move to First', MdVerticalAlignTop, onMoveToFirst)}
        {actionBtn('Move Up', MdArrowUpward, onMoveUp)}
        {actionBtn('Move Down', MdArrowDownward, onMoveDown)}
        {actionBtn('Move to Last', MdVerticalAlignBottom, onMoveToLast)}
      </HStack>
      <Box w="1px" h={4} bg="gray.100" flexShrink={0} mx={0.5} />
      {actionBtn('Delete selected', MdDelete, onDelete, 'red')}
      <Box w="1px" h={4} bg="gray.100" flexShrink={0} mx={0.5} />
      <Button
        size="xs" variant="ghost" colorScheme="gray" borderRadius="lg"
        leftIcon={<MdClose size={12} />} h={7} px={2.5} fontWeight="500"
        color="gray.500" _hover={{ bg: 'gray.100', color: 'gray.700' }}
        onClick={onClear} flexShrink={0}
      >
        Clear
      </Button>
    </MotionFlex>
  );
};

// ─── Main OrganizePdf component (content-only, self-contained) ─────────────────

const OrganizePdf = () => {
  // ── File state ──────────────────────────────────────────────────────────────
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [fileName, setFileName] = useState('');
  const nameInputRef = useRef(null);

  // ── Organizer ───────────────────────────────────────────────────────────────
  const {
    pages, selectedIds, initPages, reorderPages,
    deletePage, deleteSelected, toggleSelect,
    deselectAll, moveUp, moveDown, moveToFirst, moveToLast, reset,
  } = usePdfOrganizer();

  const [activeId, setActiveId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [zoom, setZoom] = useState(75);

  const canvasWidth = Math.round(BASE_CANVAS_W * zoom / 100);
  const cardMinWidth = canvasWidth + 20;

  const zoomOut = () => {
    const idx = ZOOM_PRESETS.indexOf(zoom);
    if (idx > 0) setZoom(ZOOM_PRESETS[idx - 1]);
  };
  const zoomIn = () => {
    const idx = ZOOM_PRESETS.indexOf(zoom);
    if (idx < ZOOM_PRESETS.length - 1) setZoom(ZOOM_PRESETS[idx + 1]);
  };

  const toast = useToast({ position: 'bottom-right' });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── File loading ────────────────────────────────────────────────────────────

  const handleFileLoad = async (file) => {
    setIsLoadingFile(true);
    try {
      const buffer = await readFileAsArrayBuffer(file);
      const doc = await pdfjsLib.getDocument({ data: buffer.slice(0) }).promise;
      setPdfFile(file);
      setArrayBuffer(buffer);
      setPdfDoc(doc);
      setFileName(file.name);
      initPages(doc.numPages);
    } catch (err) {
      const isEncrypted =
        err?.name === 'PasswordException' ||
        err?.message?.toLowerCase().includes('password');
      toast({
        title: isEncrypted ? 'Password-protected PDF' : 'Could not open PDF',
        description: isEncrypted
          ? 'This PDF is encrypted. Password-protected files are not supported.'
          : 'Make sure the file is a valid, unencrypted PDF.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingFile(false);
    }
  };

  const handleUnload = () => {
    if (pdfDoc) { try { pdfDoc.destroy(); } catch { /* ignore */ } }
    setPdfFile(null);
    setPdfDoc(null);
    setArrayBuffer(null);
    setFileName('');
    reset();
  };

  useEffect(() => {
    const handler = (e) => {
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedIds.size > 0 &&
        document.activeElement.tagName !== 'INPUT' &&
        document.activeElement.tagName !== 'TEXTAREA'
      ) {
        deleteSelected();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIds, deleteSelected]);

  const handleDragStart = useCallback(({ active }) => setActiveId(active.id), []);
  const handleDragEnd = useCallback(({ active, over }) => {
    if (over && active.id !== over.id) reorderPages(active.id, over.id);
    setActiveId(null);
  }, [reorderPages]);

  const handleExport = async () => {
    if (pages.length === 0) return;
    setIsExporting(true);
    try {
      const pageIndices = pages.map((p) => p.originalIndex);
      await exportReorderedPdf(arrayBuffer, pageIndices, fileName || pdfFile.name);
      toast({
        title: 'Downloaded',
        description: `${pages.length} page${pages.length !== 1 ? 's' : ''} exported.`,
        status: 'success', duration: 3000, isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Export failed', description: err.message ?? 'Something went wrong.',
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    if (pdfDoc) initPages(pdfDoc.numPages);
    deselectAll();
  };

  const commitName = () => {
    const trimmed = fileName.trim();
    if (!trimmed) setFileName(pdfFile?.name ?? '');
    else if (!trimmed.toLowerCase().endsWith('.pdf')) setFileName(trimmed + '.pdf');
    else setFileName(trimmed);
  };

  const activeDragPage = activeId ? pages.find((p) => p.id === activeId) : null;
  const activeDragDisplayNumber = activeDragPage ? pages.indexOf(activeDragPage) + 1 : null;

  // ── No file loaded → show upload area ──────────────────────────────────────

  if (!pdfDoc) {
    return <UploadArea onDrop={handleFileLoad} isLoading={isLoadingFile} />;
  }

  // ── File loaded → organize workspace ───────────────────────────────────────

  return (
    <Flex flex={1} minH={0}>

      {/* ── Center column ── */}
      <Flex flex={1} flexDirection="column" minW={0}>

        {/* Selection action bar — sticky below shell toolbar */}
        <AnimatePresence>
          {selectedIds.size > 0 && (
            <Box px={{ base: 4, md: 6 }} pt={4} bg="gray.50" position="sticky" top="64px" zIndex={9}>
              <SelectionActionBar
                count={selectedIds.size}
                onMoveToFirst={moveToFirst}
                onMoveUp={moveUp}
                onMoveDown={moveDown}
                onMoveToLast={moveToLast}
                onDelete={deleteSelected}
                onClear={deselectAll}
              />
            </Box>
          )}
        </AnimatePresence>

        {/* Scrollable grid area */}
        <Box flex={1} px={{ base: 4, md: 6 }} pt={4} pb={6} overflow="auto">
          {/* Controls row */}
          <Flex align="center" justify="flex-end" mb={5}>
            <HStack spacing={0}>
              <IconButton
                icon={<MdRemove size={14} />} aria-label="Zoom out" size="xs" variant="ghost"
                colorScheme="gray" borderRadius="md" isDisabled={zoom === ZOOM_PRESETS[0]} onClick={zoomOut}
              />
              <Menu placement="bottom-end">
                <MenuButton
                  as={Button} size="xs" variant="ghost" colorScheme="gray"
                  rightIcon={<MdArrowDropDown size={14} />} minW="58px" fontWeight="500" fontSize="xs" px={2}
                >
                  {zoom}%
                </MenuButton>
                <MenuList minW="90px" py={1} shadow="lg" fontSize="sm">
                  {ZOOM_PRESETS.map((p) => (
                    <MenuItem key={p} fontWeight={zoom === p ? '600' : '400'} color={zoom === p ? 'blue.600' : 'gray.700'} onClick={() => setZoom(p)}>
                      {p}%
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              <IconButton
                icon={<MdAdd size={14} />} aria-label="Zoom in" size="xs" variant="ghost"
                colorScheme="gray" borderRadius="md" isDisabled={zoom === ZOOM_PRESETS[ZOOM_PRESETS.length - 1]} onClick={zoomIn}
              />
            </HStack>
          </Flex>

          {/* Page grid or empty state */}
          {pages.length === 0 ? (
            <AllPagesRemovedState onReset={handleUnload} />
          ) : (
            <DndContext
              sensors={sensors} collisionDetection={closestCenter}
              onDragStart={handleDragStart} onDragEnd={handleDragEnd}
            >
              <SortableContext items={pages.map((p) => p.id)} strategy={rectSortingStrategy}>
                <SimpleGrid templateColumns={`repeat(auto-fill, ${cardMinWidth}px)`} spacing={4}>
                  {pages.map((page, i) => (
                    <MotionBox
                      key={page.id} layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                    >
                      <PageThumbnail
                        page={page} pdfDoc={pdfDoc} displayNumber={i + 1}
                        isSelected={selectedIds.has(page.id)}
                        onSelect={toggleSelect} onDelete={deletePage}
                        canvasWidth={canvasWidth}
                      />
                    </MotionBox>
                  ))}
                </SimpleGrid>
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragPage && <DragOverlayCard pageNumber={activeDragDisplayNumber} />}
              </DragOverlay>
            </DndContext>
          )}
        </Box>
      </Flex>

      {/* ── Right panel ── */}
      <Box
        w="210px" minW="210px" bg="white" borderLeftWidth="1px" borderColor="gray.100"
        display={{ base: 'none', lg: 'flex' }} flexDirection="column" p={4}
      >
        {/* File info */}
        <VStack align="stretch" spacing={1} mb={4}>
          <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.1em" mb={1}>
            File
          </Text>
          <Flex align="center" gap={2}>
            <Icon as={MdPictureAsPdf} color="red.400" boxSize={4} flexShrink={0} />
            <Input
              ref={nameInputRef}
              value={fileName}
              size="xs" variant="flushed" fontWeight="600" fontSize="sm" color="gray.700"
              borderColor="gray.200"
              _hover={{ borderColor: 'gray.400' }}
              _focus={{ borderColor: 'blue.500' }}
              onChange={(e) => setFileName(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') nameInputRef.current?.blur();
                if (e.key === 'Escape') { setFileName(pdfFile?.name ?? ''); nameInputRef.current?.blur(); }
              }}
            />
          </Flex>
          {pages.length > 0 && (
            <Text fontSize="xs" color="gray.400" pl={6}>
              {pages.length} {pages.length === 1 ? 'page' : 'pages'}
            </Text>
          )}
        </VStack>

        <Divider borderColor="gray.100" mb={4} />

        {/* Actions */}
        <VStack spacing={2} align="stretch">
          <Button
            leftIcon={<MdDownload />} colorScheme="blue" borderRadius="lg" fontWeight="600"
            size="sm" isLoading={isExporting} loadingText="Downloading…"
            isDisabled={pages.length === 0} onClick={handleExport} w="full"
          >
            Download
          </Button>
          <Button
            leftIcon={<MdRefresh />} variant="outline" colorScheme="gray" borderRadius="lg"
            fontWeight="600" size="sm" isDisabled={pages.length === 0} onClick={handleReset} w="full"
          >
            Reset
          </Button>
          <Button
            leftIcon={<MdUploadFile />} variant="ghost" colorScheme="gray" borderRadius="lg"
            fontWeight="500" size="sm" onClick={handleUnload} w="full"
          >
            Change file
          </Button>
        </VStack>

        <Box mt="auto" pt={6}>
          <Flex align="flex-start" gap={2} p={3} bg="gray.50" borderRadius="12px">
            <Icon as={MdPictureAsPdf} color="gray.300" boxSize={3.5} mt={0.5} flexShrink={0} />
            <Text fontSize="10px" color="gray.400" lineHeight="tall">
              All processing happens locally. Your files never leave this device.
            </Text>
          </Flex>
        </Box>
      </Box>

    </Flex>
  );
};

export default OrganizePdf;
